import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../lib/apiClient";
import { API_BASE_URL } from "../../config";
import { Crosshair } from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

/* ── Constants (shared with AddPropertyPage) ── */
const MAX_IMAGES = 12;
const MAX_SIZE_MB = 8;
const PROPERTY_TYPES = [
  "APARTMENT",
  "HOUSE",
  "CONDO",
  "VILLA",
  "STUDIO",
  "SINGLE_ROOM",
  "COMMERCIAL_SPACE",
  "OFFICE_SPACE",
  "OTHER",
];
const FURNISHED = ["FURNISHED", "UNFURNISHED", "SEMI_FURNISHED"];
const PET_POLICY = ["ALLOWED", "NOT_ALLOWED", "NEGOTIABLE"];
const amenityToggles = [
  { key: "parkingAvailable", label: "Parking" },
  { key: "balcony", label: "Balcony" },
  { key: "swimmingPool", label: "Swimming Pool" },
  { key: "gym", label: "Gym" },
  { key: "heating", label: "Heating" },
  { key: "airConditioning", label: "Air Conditioning" },
];

function safeNumber(v) {
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
function isValidUrl(u) {
  return /^https?:\/\/[^\s]+$/i.test(u.trim());
}
function toDateStr(val) {
  if (!val) return "";
  try {
    return new Date(val).toISOString().split("T")[0];
  } catch {
    return "";
  }
}

export default function EditPropertyPage() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const [isLocationLocked, setIsLocationLocked] = useState(true);
  const [locating, setLocating] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 27.7105, lng: 85.3488 });

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
    libraries: [],
  });

  const [form, setForm] = useState(null); // null until loaded
  const [boolFields, setBoolFields] = useState({
    parkingAvailable: false,
    balcony: false,
    swimmingPool: false,
    gym: false,
    heating: false,
    airConditioning: false,
  });

  /* Images */
  const [existingImages, setExistingImages] = useState([]); // {id, imageUrl/url}
  const [newImages, setNewImages] = useState([]); // {file, previewUrl}
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  /* ── Load property ── */
  const load = useCallback(async () => {
    try {
      const prop = await apiRequest(`/properties/${propertyId}`);
      setForm({
        title: prop.title || "",
        description: prop.description || "",
        price: prop.price != null ? String(prop.price) : "",
        deposit: prop.deposit != null ? String(prop.deposit) : "",
        location: prop.location || "",
        city: prop.city || "",
        stateProvince: prop.stateProvince || "",
        postalCode: prop.postalCode || "",
        neighborhoodInfo: prop.neighborhoodInfo || "",
        propertyType: prop.propertyType || "APARTMENT",
        bedrooms: prop.bedrooms != null ? String(prop.bedrooms) : "",
        bathrooms: prop.bathrooms != null ? String(prop.bathrooms) : "",
        area: prop.area != null ? String(prop.area) : "",
        furnishedStatus: prop.furnishedStatus || "UNFURNISHED",
        floorLevel: prop.floorLevel != null ? String(prop.floorLevel) : "",
        totalFloors: prop.totalFloors != null ? String(prop.totalFloors) : "",
        leaseDuration:
          prop.leaseDuration != null ? String(prop.leaseDuration) : "",
        maintenanceFee:
          prop.maintenanceFee != null ? String(prop.maintenanceFee) : "",
        amenities: prop.amenities || "",
        petPolicy: prop.petPolicy || "ALLOWED",
        securityFeatures: prop.securityFeatures || "",
        videoTourUrl: prop.videoTourUrl || "",
        floorPlanUrl: prop.floorPlanUrl || "",
        energyEfficiencyRating: prop.energyEfficiencyRating || "",
        availableFrom: toDateStr(prop.availableFrom),
        latitude: prop.latitude != null ? String(prop.latitude) : "27.7105",
        longitude: prop.longitude != null ? String(prop.longitude) : "85.3488",
      });
      const loadedLat = parseFloat(prop.latitude ?? 27.7105);
      const loadedLng = parseFloat(prop.longitude ?? 85.3488);
      setMapCenter({ lat: loadedLat, lng: loadedLng });
      setIsLocationLocked(true);
      setBoolFields({
        parkingAvailable: !!prop.parkingAvailable,
        balcony: !!prop.balcony,
        swimmingPool: !!prop.swimmingPool,
        gym: !!prop.gym,
        heating: !!prop.heating,
        airConditioning: !!prop.airConditioning,
      });
      // images may come as {id, url} or {id, imageUrl}
      const imgs = Array.isArray(prop.images) ? prop.images : [];
      setExistingImages(
        imgs.map((img) => ({ id: img.id, url: img.url || img.imageUrl || "" })),
      );
    } catch (err) {
      setLoadError(err?.message || "Failed to load property.");
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Helpers ── */
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleBool = (key) =>
    setBoolFields((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── Images ── */
  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - existingImages.length - newImages.length;
    const toAdd = files.slice(0, remaining).filter((f) => {
      if (f.size / 1024 / 1024 > MAX_SIZE_MB) {
        alert(`"${f.name}" exceeds ${MAX_SIZE_MB} MB and was skipped.`);
        return false;
      }
      return true;
    });
    const previews = toAdd.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewImages((prev) => [...prev, ...previews]);
    e.target.value = "";
  };

  const removeExistingImage = (id) => {
    setDeletedImageIds((prev) => [...prev, id]);
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  /* ── Map / location ── */
  const handleMyLocation = () => {
    if (locating || isLocationLocked) return;
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        set("latitude", String(lat));
        set("longitude", String(lng));
        setMapCenter({ lat, lng });
        mapRef.current?.panTo({ lat, lng });
        setLocating(false);
      },
      () => {
        alert(
          "Could not get your location. Please allow location access and try again.",
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const toggleLocationLock = () => {
    if (!isLocationLocked) {
      setIsLocationLocked(true);
      setErrors((prev) => {
        const { latitude, longitude, ...rest } = prev;
        return rest;
      });
    } else {
      setIsLocationLocked(false);
    }
  };

  /* ── Validation ── */
  const validate = () => {
    const errs = {};
    ["title", "description", "price", "location", "city"].forEach((k) => {
      if (!form[k]?.trim()) errs[k] = "Required";
    });
    if (form.videoTourUrl.trim() && !isValidUrl(form.videoTourUrl))
      errs.videoTourUrl = "Invalid URL";
    if (form.floorPlanUrl.trim() && !isValidUrl(form.floorPlanUrl))
      errs.floorPlanUrl = "Invalid URL";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError("");

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      price: safeNumber(form.price),
      deposit: safeNumber(form.deposit),
      location: form.location.trim(),
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      city: form.city.trim(),
      stateProvince: form.stateProvince.trim(),
      postalCode: form.postalCode.trim(),
      neighborhoodInfo: form.neighborhoodInfo.trim(),
      propertyType: form.propertyType,
      bedrooms: safeNumber(form.bedrooms),
      bathrooms: safeNumber(form.bathrooms),
      area: safeNumber(form.area),
      furnishedStatus: form.furnishedStatus,
      floorLevel: safeNumber(form.floorLevel),
      totalFloors: safeNumber(form.totalFloors),
      availableFrom: form.availableFrom,
      leaseDuration: safeNumber(form.leaseDuration),
      maintenanceFee: safeNumber(form.maintenanceFee),
      amenities: form.amenities.trim(),
      petPolicy: form.petPolicy,
      securityFeatures: form.securityFeatures.trim(),
      videoTourUrl: form.videoTourUrl.trim(),
      floorPlanUrl: form.floorPlanUrl.trim(),
      energyEfficiencyRating: form.energyEfficiencyRating.trim(),
      ...boolFields,
    };

    const formData = new FormData();
    formData.append("property", JSON.stringify(payload));
    newImages.forEach(({ file }) => formData.append("images", file));
    if (deletedImageIds.length > 0) {
      formData.append("deletedImages", JSON.stringify(deletedImageIds));
    }

    try {
      const token = localStorage.getItem("rh_access_token");
      // Mobile uses POST to /properties/:id — mirror that
      const res = await fetch(`${API_BASE_URL}/properties/${propertyId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errBody = await res.text();
        let msg;
        try {
          msg = JSON.parse(errBody)?.message || errBody;
        } catch {
          msg = errBody;
        }
        throw new Error(msg || `Server error ${res.status}`);
      }

      navigate("/owner/listings", {
        state: { toast: "Property updated successfully!" },
      });
    } catch (err) {
      setSubmitError(err?.message || "Failed to update. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Render guards ── */
  if (loadError)
    return (
      <div className="owner-page">
        <p className="form-error">{loadError}</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    );
  if (!form)
    return (
      <div className="owner-page">
        <p className="loading-message">Loading property…</p>
      </div>
    );

  /* ── Field components ── */
  const F = ({ label, name, type = "text", placeholder }) => (
    <div className={`prop-field${errors[name] ? " prop-field--error" : ""}`}>
      <label className="prop-label">{label}</label>
      <input
        className="prop-input"
        type={type}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
        placeholder={placeholder || ""}
      />
      {errors[name] && <p className="prop-field-error">{errors[name]}</p>}
    </div>
  );
  const T = ({ label, name, rows = 4, placeholder }) => (
    <div className={`prop-field${errors[name] ? " prop-field--error" : ""}`}>
      <label className="prop-label">{label}</label>
      <textarea
        className="prop-input prop-textarea"
        rows={rows}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
        placeholder={placeholder || ""}
      />
    </div>
  );
  const S = ({ label, name, options }) => (
    <div className="prop-field">
      <label className="prop-label">{label}</label>
      <select
        className="prop-input prop-select"
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </div>
  );

  const totalImgCount = existingImages.length + newImages.length;

  return (
    <div className="owner-page">
      <div className="owner-page-header">
        <h1 className="owner-page-title">Edit Property</h1>
        <p className="owner-page-subtitle">{form.title}</p>
      </div>

      <form onSubmit={handleSubmit} className="prop-form" noValidate>
        {/* Basic */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Basic Information</legend>
          <div className="prop-grid-2">
            <F label="Title *" name="title" placeholder="Property title" />
            <S
              label="Property Type"
              name="propertyType"
              options={PROPERTY_TYPES}
            />
          </div>
          <T
            label="Description *"
            name="description"
            placeholder="Describe the property…"
          />
        </fieldset>

        {/* Pricing */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Pricing</legend>
          <div className="prop-grid-3">
            <F
              label="Monthly Rent (NPR) *"
              name="price"
              type="number"
              placeholder="15000"
            />
            <F
              label="Security Deposit (NPR)"
              name="deposit"
              type="number"
              placeholder="30000"
            />
            <F
              label="Maintenance Fee (NPR)"
              name="maintenanceFee"
              type="number"
              placeholder="500"
            />
          </div>
        </fieldset>

        {/* Location */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Location</legend>
          <div className="prop-grid-2">
            <F
              label="Full Address *"
              name="location"
              placeholder="Kumaripati, Lalitpur"
            />
            <F label="City *" name="city" placeholder="Lalitpur" />
          </div>
          <div className="prop-grid-3">
            <F
              label="State / Province"
              name="stateProvince"
              placeholder="Bagmati"
            />
            <F label="Postal Code" name="postalCode" placeholder="44600" />
            <F
              label="Neighborhood Info"
              name="neighborhoodInfo"
              placeholder="Near City Centre"
            />
          </div>
          {/* Map picker */}
          <div className="prop-map-header">
            <span className="prop-label">Pin Your Property Location</span>
            <button
              type="button"
              className="prop-map-locate-btn"
              onClick={handleMyLocation}
              disabled={locating || isLocationLocked}
            >
              <Crosshair size={14} strokeWidth={2} />
              {locating ? "Locating\u2026" : "Use My Location"}
            </button>
          </div>

          <div
            className={`prop-map-wrap${
              isLocationLocked ? " prop-map-wrap--locked" : ""
            }`}
          >
            {!mapsLoaded ? (
              <div className="prop-map-loading">Loading map…</div>
            ) : (
              <GoogleMap
                mapContainerClassName="prop-map"
                center={mapCenter}
                zoom={15}
                onClick={(e) => {
                  if (!isLocationLocked) {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    set("latitude", String(lat));
                    set("longitude", String(lng));
                  }
                }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  gestureHandling: "greedy",
                }}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
              >
                <Marker
                  position={{
                    lat: Number(form.latitude),
                    lng: Number(form.longitude),
                  }}
                  draggable={!isLocationLocked}
                  onDragEnd={(e) => {
                    set("latitude", String(e.latLng.lat()));
                    set("longitude", String(e.latLng.lng()));
                  }}
                />
              </GoogleMap>
            )}

            {/* Coordinates badge */}
            <div className="prop-map-coords">
              {Number(form.latitude).toFixed(5)},{" "}
              {Number(form.longitude).toFixed(5)}
            </div>

            {/* Floating confirm / edit button */}
            <button
              type="button"
              className={`prop-map-confirm-btn${
                isLocationLocked ? " prop-map-confirm-btn--locked" : ""
              }`}
              onClick={toggleLocationLock}
            >
              {isLocationLocked ? "Edit Location" : "Confirm Location"}
            </button>
          </div>

          {errors.latitude && (
            <p className="prop-field-error" style={{ marginTop: "0.4rem" }}>
              {errors.latitude}
            </p>
          )}
        </fieldset>

        {/* Details */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Property Details</legend>
          <div className="prop-grid-3">
            <F label="Bedrooms" name="bedrooms" type="number" placeholder="2" />
            <F
              label="Bathrooms"
              name="bathrooms"
              type="number"
              placeholder="1"
            />
            <F
              label="Area (sq ft)"
              name="area"
              type="number"
              placeholder="800"
            />
          </div>
          <div className="prop-grid-3">
            <F
              label="Floor Level"
              name="floorLevel"
              type="number"
              placeholder="2"
            />
            <F
              label="Total Floors"
              name="totalFloors"
              type="number"
              placeholder="4"
            />
            <F
              label="Lease Duration (months)"
              name="leaseDuration"
              type="number"
              placeholder="12"
            />
          </div>
          <div className="prop-grid-3">
            <S
              label="Furnished Status"
              name="furnishedStatus"
              options={FURNISHED}
            />
            <S label="Pet Policy" name="petPolicy" options={PET_POLICY} />
            <F label="Available From" name="availableFrom" type="date" />
          </div>
        </fieldset>

        {/* Amenities */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Amenities</legend>
          <div className="prop-toggles">
            {amenityToggles.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`prop-toggle${boolFields[key] ? " prop-toggle--on" : ""}`}
                onClick={() => toggleBool(key)}
              >
                {boolFields[key] ? "✓ " : ""}
                {label}
              </button>
            ))}
          </div>
          <T
            label="Other Amenities"
            name="amenities"
            rows={2}
            placeholder="e.g. 24/7 security, backup power…"
          />
          <div className="prop-grid-2">
            <F
              label="Security Features"
              name="securityFeatures"
              placeholder="CCTV, gated compound…"
            />
            <F
              label="Energy Efficiency Rating"
              name="energyEfficiencyRating"
              placeholder="A, B, C…"
            />
          </div>
        </fieldset>

        {/* Media */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Media</legend>
          <div className="prop-grid-2">
            <div
              className={`prop-field${errors.videoTourUrl ? " prop-field--error" : ""}`}
            >
              <label className="prop-label">Video Tour URL</label>
              <input
                className="prop-input"
                type="url"
                value={form.videoTourUrl}
                onChange={(e) => set("videoTourUrl", e.target.value)}
                placeholder="https://youtube.com/…"
              />
              {errors.videoTourUrl && (
                <p className="prop-field-error">{errors.videoTourUrl}</p>
              )}
            </div>
            <div
              className={`prop-field${errors.floorPlanUrl ? " prop-field--error" : ""}`}
            >
              <label className="prop-label">Floor Plan URL</label>
              <input
                className="prop-input"
                type="url"
                value={form.floorPlanUrl}
                onChange={(e) => set("floorPlanUrl", e.target.value)}
                placeholder="https://…"
              />
              {errors.floorPlanUrl && (
                <p className="prop-field-error">{errors.floorPlanUrl}</p>
              )}
            </div>
          </div>

          {/* Images */}
          <div className="prop-field">
            <label className="prop-label">
              Photos{" "}
              <span className="prop-hint-inline">
                ({totalImgCount}/{MAX_IMAGES}, max {MAX_SIZE_MB}MB each)
              </span>
            </label>
            <div className="prop-image-grid">
              {existingImages.map((img) => (
                <div key={img.id} className="prop-img-thumb">
                  <img src={img.url} alt="existing" />
                  <button
                    type="button"
                    className="prop-img-remove"
                    onClick={() => removeExistingImage(img.id)}
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {newImages.map(({ previewUrl }, idx) => (
                <div
                  key={`new-${idx}`}
                  className="prop-img-thumb prop-img-thumb--new"
                >
                  <img src={previewUrl} alt={`new ${idx + 1}`} />
                  <button
                    type="button"
                    className="prop-img-remove"
                    onClick={() => removeNewImage(idx)}
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {totalImgCount < MAX_IMAGES && (
                <button
                  type="button"
                  className="prop-img-add"
                  onClick={() => fileInputRef.current?.click()}
                >
                  + Add Photo
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={onPickImages}
            />
          </div>
        </fieldset>

        {submitError && (
          <p className="form-error prop-submit-error">{submitError}</p>
        )}

        <div className="prop-submit-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/owner/listings")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary prop-submit-btn"
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
